import { GraphModel } from '../graph/graph.model';
import { z } from 'zod/v3';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  type ActionTrackMetadata,
  type ActionTrackingParams,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import { AgentModel } from './agent.model';
import { Routes } from '../routes';
import { DG } from '@cotera/sdk/core';
import { saveAgentChatId } from './agent-chat-state';

/**
 * Format a CommitsBuildFailure error into a user-friendly message
 */
const formatBuildFailure = (failure: DG.CommitsBuildFailure): string => {
  switch (failure.t) {
    case 'symbol-taken':
      return `The name "${failure.sym}" is already in use. Please choose a different name.`;
    case 'circular-reference':
      return `Circular reference detected: "${failure.from}" cannot reference "${failure.to}" because it would create a loop.`;
    default:
      return DG.prettyPrintCommitsBuildFailure(failure);
  }
};

/**
 * Format save errors into user-friendly messages
 */
const formatSaveError = (
  error:
    | {
        t: 'build';
        e: DG.CommitsBuildFailure | { t: string; message?: string };
      }
    | { t: 'tc'; e: unknown }
): string => {
  if (error.t === 'build') {
    const buildError = error.e;
    // Check if this is a CommitsBuildFailure (has specific known types)
    if (
      typeof buildError === 'object' &&
      buildError !== null &&
      't' in buildError
    ) {
      const knownTypes = [
        'symbol-taken',
        'symbol-not-mapped',
        'ident-locked',
        'col-already-exists',
        'col-not-found',
        'can-only-add-col-to-rel',
        'can-only-modify-cols-on-rels',
        'cant-update-different-kind',
        'circular-reference',
      ];
      if (knownTypes.includes(buildError.t)) {
        return formatBuildFailure(buildError as DG.CommitsBuildFailure);
      }
      // Custom error from makeOpsFromState
      if ('message' in buildError && typeof buildError.message === 'string') {
        return buildError.message;
      }
    }
    return 'Failed to save agent. Please try again.';
  }
  return 'Failed to save agent due to a type error. Please check your configuration.';
};

type Params = {};

export class SaveAgentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Save Agent';
  description = 'Save current agent as a tool';
  inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = 'Agent';
  icon = 'save';

  constructor(
    private readonly navigator: {
      pushAndReplace: (props: { route: string }) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return (
      context.isInScope(GraphModel) &&
      context.hasTargetedModelOfType(AgentModel)
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graph = context.getInScopeModelOfType(GraphModel);
    const agent = context.targetedModelOfType(AgentModel);

    Assert.assert(graph !== null, 'Graph not found');
    Assert.assert(agent !== null, 'Agent not found');

    const saveToast = toast.loading('Saving agent...', {
      id: 'save-agent',
    });

    const result = agent.compile({ setOperations: true });

    if (result.isErr()) {
      saveToast.error(formatSaveError(result.error));
      return err([
        { t: result.error.t, message: JSON.stringify(result.error.e) },
      ]);
    }

    if (agent.newOperations.snapshot().length === 0) {
      saveToast.done();
      return ok({});
    }

    // Capture name change state before staging (changes.name resets after markAsStaged)
    const nameHasChanged = agent.changes.snapshot()['name'];

    const stagingResult = await graph.attemptToStageOperations(
      agent.newOperations.snapshot()
    );

    if (stagingResult.isErr()) {
      const error = {
        t: stagingResult.error.err.t,
        message: JSON.stringify(stagingResult.error.err),
      };

      saveToast.error(formatBuildFailure(stagingResult.error.err));
      return err([error]);
    }

    agent.markAsStaged();

    // After staging, the chat transport sends the graph identity as
    // `context.agent`; ensureConversation backfills `chats.agent` on the next
    // message — no separate association call needed.

    saveToast.success(`Agent ${agent.name.snapshot()} saved`);

    // Update tab/URL for new agents OR when existing agent's name changed
    if (agent.opts.isNew || nameHasChanged) {
      // Save chatId so messages can be fetched from server after navigation
      saveAgentChatId(agent.name.snapshot(), agent.chatId.snapshot());

      this.navigator.pushAndReplace({
        route: Routes.agent.forId(agent.name.snapshot()).toString(),
      });
    }

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    return (
      context
        .targetedModelOfType(AgentModel)
        ?.editorState.snapshot()
        .title() === null
    );
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const model = context.targetedModelOfType(AgentModel);

    Assert.assert(model !== null);

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                model={model}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }

  track({
    context,
  }: ActionTrackingParams<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const agent = context.targetedModelOfType(AgentModel);
    if (agent === null) {
      return null;
    }

    const changes = agent.changes.snapshot();

    return {
      event: 'agent_save_clicked',
      properties: {
        agentId: agent.id,
        agentName: agent.name.snapshot(),
        hasPromptChanges: changes['prompt'],
        hasModelChanges: changes['model'],
        hasNameChanges: changes['name'],
        hasOutputShapeChanges: changes['outputShape'],
        isNew: agent.opts.isNew ?? false,
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  model: AgentModel;
}> = ({ onRespond, model }) => {
  const [name, setName] = useState(model.name.snapshot());
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView title={`Save agent as`} className="px-4 py-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="agent-name" className="text-sm">
            Agent Name
          </Label>
          <Input
            id="agent-name"
            placeholder="name"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            disabled={loading}
            type="submit"
            variant="default"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              const state = new DG.DocumentEditor.EditorState(
                model.formState.snapshot().agentState
              )
                .setTitle(name)
                .getState();
              model.formState.set({
                ...model.formState.snapshot(),
                agentState: state,
              });
              await onRespond({});
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
